import React, { useEffect, useRef, useState } from "react";

import { Button, IconButton, TextField } from "@mui/material";
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import "ag-grid-enterprise";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import EditIcon from "@mui/icons-material/Edit";
import PrintIcon from "@mui/icons-material/Print";
import VisibilityIcon from "@mui/icons-material/Visibility";

import SideNav from "component/layouts/sideNav";
import {
  closureStatusRenderer,
  getItemList,
  getItems,
  getProjectManagers,
  getSites,
  getUoms,
  projectManagerCellRenderer,
  setItemCategories,
  setItemList,
  setItems,
  setProjectManagers,
  setSites,
  setUoms,
  siteCellRenderer,
} from "component/cellRenderers/renderers";
import {
  setAlertConfirmDeletion,
  setAlertTitleText,
  setAlertRadioConfirmation,
} from "component/modal/alertModal";

import {
  ADD,
  DELETE,
  GO,
  INDENT,
  MATERIAL_MANAGEMENT,
} from "resources/constant/strings/string";
import {
  LIGHT_GREY_BACKGROUND,
  PRUSSIAN_BLUE,
  SPANISH_GREEN,
  WHITE,
} from "resources/constant/colors/theme";
import {
  PATH_LOGIN,
  PATH_INDENT,
  PATH_INDENT_DETAILS,
} from "resources/constant/url/url";

import { APILOGIN } from "apihandler/login";
import { APIMASTER } from "apihandler/master";
import { APIMATERIALMGMT } from "apihandler/materialmgmt";

import {
  setFilters,
  setRows,
  saveGridState,
  setHydrated,
} from "store/Slice/indentSlice";
import { useDebounce } from "resources/constant/utilities/util";

const getCacheKey = (fromDate, toDate) =>
  `${fromDate.format("YYYY-MM-DD")}_${toDate.format("YYYY-MM-DD")}`;

export default function Indent() {
  const navigate = useNavigate();
  const gridRef = useRef(null);
  const indentCacheRef = useRef({});
  const { date } = useParams();

  const dispatch = useDispatch();
  const { fromDate, toDate, rows, grid: gridState, hydrated } = useSelector(
    (s) => s.indents
  );

  const [selectedRowId, setSelectedRowId] = useState(0);
  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [dateValue, setDateValue] = useState(
    fromDate ? dayjs(fromDate) : date ? dayjs(date) : dayjs().startOf("month")
  );
  const [toDateValue, setToDateValue] = useState(
    toDate ? dayjs(toDate) : dayjs().endOf("month")
  );

  useEffect(() => {
    if (!APILOGIN.GetIsLoggedIn()) navigate(PATH_LOGIN);
  }, [navigate]);

  const columnDefs = [
    {
      flex: 1.5,
      headerName: "Indent No",
      field: "indentNo",
      checkboxSelection: false,
      cellStyle: {
        "border-right-color": "#e2e2e2",
      },
      wrapText: true,
      autoHeight: true,
    },
    {
      flex: 0.8,
      headerName: "Date",
      field: "date",
      filter: false,
      floatingFilter: false,
      cellStyle: {
        "text-align": "center",
        "border-right-color": "#e2e2e2",
      },
    },
    {
      flex: 1.5,
      headerName: "Project Manager",
      field: "projectManager",
      autoHeight: true,
      wrapText: true,
      valueGetter: (params) => {
        return projectManagerCellRenderer({
          value: params.data.projectManager,
        });
      },
      cellStyle: {
        "border-right-color": "#e2e2e2",
      },
    },
    {
      flex: 1.5,
      headerName: "Site",
      field: "siteId",
      autoHeight: true,
      wrapText: true,
      valueGetter: (params) => {
        return siteCellRenderer({ value: params.data.siteId });
      },
      cellStyle: {
        "border-right-color": "#e2e2e2",
      },
    },
    {
      headerName: "Status",
      field: "status",
      autoHeight: true,
      wrapText: true,
      valueGetter: (params) => {
        return closureStatusRenderer({ value: params.data.status });
      },
      cellStyle: {
        "border-right-color": "#e2e2e2",
      },
    },
    {
      headerName: "PO Generated",
      field: "hasPurchaseOrder",
      autoHeight: true,
      wrapText: true,
      valueGetter: (params) => {
        return params.data.hasPurchaseOrder ? "Yes" : "No";
      },
      cellStyle: {
        "border-right-color": "#e2e2e2",
      },
    },
    {
      flex: 1,
      headerName: "Details",
      field: "id",
      sortable: false,
      filter: false,
      floatingFilter: false,
      cellRenderer: (params) => (
        <div style={{ display: "flex" }}>
          {params.data.status !== "C" && !params.data.hasPurchaseOrder && (
            <IconButton
              aria-label="edit"
              onClick={() => {
                captureGridState();
                navigate(PATH_INDENT_DETAILS + params.data.id, {
                  state: {
                    date: (dateValue || dayjs()).format("YYYY-MM-DD"),
                    isEdit: true,
                  },
                });
              }}
            >
              <EditIcon style={{ color: PRUSSIAN_BLUE, width: "20px" }} />
            </IconButton>
          )}
          <IconButton
            aria-label="view"
            onClick={() => {
              captureGridState();
              navigate(PATH_INDENT_DETAILS + params.data.id, {
                state: {
                  date: (dateValue || dayjs()).format("YYYY-MM-DD"),
                  isEdit: false,
                },
              });
            }}
          >
            <VisibilityIcon style={{ color: PRUSSIAN_BLUE, width: "20px" }} />
          </IconButton>

          <IconButton
            aria-label="print"
            onClick={() => {
              captureGridState();
              let filename = "";
              APIMATERIALMGMT.Indent(params.data.id)
                .then((resp) => resp.json())
                .then((resp) => {
                  console.log("hola4", resp.response);
                  let itemDetails = (resp.response?.itemDetails || []).sort(
                    (a, b) => a.id - b.id
                  );
                  const items = getItems();
                  const itemList = getItemList();
                  const uomList = getUoms();

                  itemDetails.forEach((v, i) => {
                    const it = items.filter((y) => y.value === v.itemId);
                    if (it.length > 0) v.categoryId = it[0].dependentvalue;
                    v.rowId = i;

                    const itemsIn = itemList.filter((x) => x.id === v.itemId);
                    if (itemsIn.length > 0) {
                      const match = uomList.find(
                        (x) => x.value === itemsIn[0].purchaseUOMId
                      );
                      v.quantityUom = `${v.quantity} ${match ? match.name : ""}`;
                    }
                  });

                  const pms = getProjectManagers();
                  const sites = getSites();
                  const pm = pms.find((x) => x.value === resp.response.projectManager);
                  const st = sites.find((x) => x.value === resp.response.siteId);

                  const dto = {
                    id: resp.response.id,
                    indentNo: resp.response.indentNo,
                    projectManager: resp.response.projectManager,
                    siteId: resp.response.siteId,
                    status: resp.response.status,
                    date: resp.response.date?.split("-").reverse().join("-"),
                    itemDetails,
                    projectManagerName: pm ? pm.name : "",
                    siteName: st ? st.name : "",
                    exportExcel: resp.response.exportExcel,
                  };

                  return setAlertRadioConfirmation(
                    { excel: "Excel", pdf: "PDF" },
                    "Please select an option..."
                  ).then((result) => ({ dto, result }));
                })
                .then(({ dto, result }) => {
                  if (!result?.isConfirmed) return null;
                  dto.exportExcel = result.value === "excel";
                  return APIMATERIALMGMT.ExportIndent(dto).then((res) => {
                    const ct = res.headers.get("content-type") || "";
                    if (ct.startsWith("application/json")) {
                      return Promise.reject(res.json());
                    }
                    const header = res.headers.get("Content-Disposition");
                    if (header) {
                      const parts = header.split(";");
                      filename = parts[1].split("=")[1].replaceAll('"', "");
                    } else {
                      filename = "indent.html";
                    }
                    return res.blob().then((blob) => ({
                      blob,
                      exportExcel: dto.exportExcel,
                    }));
                  });
                })
                .then((payload) => {
                  if (!payload) return;
                  if (payload.exportExcel) {
                    const url = window.URL.createObjectURL(payload.blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = filename.replace(".html", ".xlsx");
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    return;
                  }
                  payload.blob.text().then((x) => {
                    const html = x.replace(
                      "<script>",
                      '<script type="text/javascript">'
                    );
                    const b = new Blob([html], { type: "text/html" });
                    const url = window.URL.createObjectURL(b, {
                      oneTimeOnly: true,
                    });
                    const win = window.open(url, "_blank", "");
                    setTimeout(() => {
                      if (win) win.print();
                    }, 2000);
                  });
                })
                .catch((m) => {
                  setAlertTitleText(
                    "Oops...",
                    `Something went wrong...${m?.message || m}`
                  );
                });
            }}
          >
            <PrintIcon style={{ color: PRUSSIAN_BLUE, width: "20px" }} />
          </IconButton>
        </div>
      ),
    },
  ];

  const defaultColDef = {
    resizable: true,
    sortable: true,
    editable: false,
    flex: 1,
    filter: "agTextColumnFilter",
    filterParams: {
      caseSensitive: false,
      defaultOption: "startsWith",
    },
    floatingFilter: true,

  };

  const fetchIndents = (d1, d2) =>
    APIMATERIALMGMT.Indents(d1.format("YYYY-MM-DD"), d2.format("YYYY-MM-DD"))
      .then((resp) => resp.json())
      .then((resp) => {
        if (resp.statusCode !== 200) {
          setAlertTitleText("Oops..", `An error occurred ${resp.message}`);
          return [];
        }
        return (resp.response || []).sort((a, b) => b.id - a.id);
      });

  const loadIndents = async (from, to) => {
    try {
      setIsLoading(true);
      if (!from || !to) return;

      // persist filters (same as now)
      dispatch(
        setFilters({
          fromDate: from.format("YYYY-MM-DD"),
          toDate: to.format("YYYY-MM-DD"),
        })
      );

      const cacheKey = getCacheKey(from, to);
      let data;

      if (indentCacheRef.current[cacheKey]) {
        data = indentCacheRef.current[cacheKey];
      } else {
        data = await fetchIndents(from, to);
        indentCacheRef.current[cacheKey] = data;
      }

      try {
        const rowData = [];
        gridRef.current.api.forEachNode((n) => rowData.push(n.data));
        if (rowData.length) {
          gridRef.current.api.applyTransaction({ remove: rowData });
        }
      } catch (_) {}

      try {
        gridRef.current.api.applyTransaction({ add: data });
        dispatch(setRows(data));
      } catch (_) {}
    } finally {
      setIsLoading(false);
    }
  };

  const debouncedLoadIndents = useDebounce(
    (from, to) => loadIndents(from, to),
    300
  );

  const onGridReady = (params) => {
    APIMASTER.ItemCategories().then((r) => r.json()).then((r) => {
      if (r.statusCode === 200) setItemCategories(r.response);
    });
    APIMASTER.SiteOptions().then((r) => r.json()).then((r) => {
      if (r.statusCode === 200) setSites(r.response);
    });
    APIMASTER.UomOptions().then((r) => r.json()).then((r) => {
      if (r.statusCode === 200) setUoms(r.response);
    });
    APIMASTER.ProjectManagerOptions().then((r) => r.json()).then((r) => {
      if (r.statusCode === 200) setProjectManagers(r.response);
    });
    APIMASTER.Items().then((r) => r.json()).then((r) => {
      if (r.statusCode !== 200) return;
      const options = r.response.map((el) => ({
        name: el.itemName,
        value: el.id,
        dependentvalue: el.categoryId,
      }));
      setItems(options);
      setItemList(r.response);
    });

    const dFrom = fromDate
      ? dayjs(fromDate)
      : date
      ? dayjs(date)
      : dayjs().startOf("month");
    const dTo = toDate ? dayjs(toDate) : dayjs().endOf("month");

    setDateValue(dFrom);
    setToDateValue(dTo);

    const paint = rows && rows.length ? Promise.resolve(rows) : fetchIndents(dFrom, dTo);

    paint.then((data) => {
      params.api.applyTransaction({ add: data });
      dispatch(setRows(data));

      if (gridState?.filterModel) params.api.setFilterModel(gridState.filterModel);
      if (gridState?.columnState)
        params.columnApi.applyColumnState({
          state: gridState.columnState,
          applyOrder: true,
        });
      if (gridState?.quickFilterText)
        params.api.setQuickFilter(gridState.quickFilterText);
      if (gridState?.selectedIds?.length) {
        params.api.forEachNode((n) => {
          if (gridState.selectedIds.includes(n.data.id)) n.setSelected(true);
        });
      }
      if (gridState?.scrollTop != null) {
        const body = document.querySelector(".ag-body-viewport");
        if (body) body.scrollTop = gridState.scrollTop;
      }
      dispatch(setHydrated(true));
    });
  };

  const onRowSelectionChanged = (event) => {
    const selected = event.api.getSelectedRows();
    if (selected.length > 0) {
      const ids = selected.map((r) => r.id);
      setSelectedRowId(selected[0].id);
      setSelectedRowIds(ids);
      dispatch(saveGridState({ selectedIds: ids }));
    } else {
      setSelectedRowId(0);
      setSelectedRowIds([]);
      dispatch(saveGridState({ selectedIds: [] }));
    }
  };

  const captureGridState = () => {
    const api = gridRef.current?.api;
    const columnApi = gridRef.current?.columnApi;
    const body = document.querySelector(".ag-body-viewport");
    dispatch(
      saveGridState({
        filterModel: api?.getFilterModel() || null,
        columnState: columnApi?.getColumnState() || null,
        quickFilterText: api?.getQuickFilter?.() || "",
        selectedIds: (api?.getSelectedRows?.() || []).map((r) => r.id),
        scrollTop: body ? body.scrollTop : 0,
      })
    );
    dispatch(
      setFilters({
        fromDate: (dateValue || dayjs()).format("YYYY-MM-DD"),
        toDate: (toDateValue || dayjs()).format("YYYY-MM-DD"),
      })
    );
  };

  const deleteModel = () => {
    if (selectedRowIds.length === 0) return;

    setAlertConfirmDeletion(
      "Confirmation needed",
      `Do you confirm on proceeding to delete ${selectedRowIds.length} record(s)?`
    ).then((consent) => {
      if (!consent.isConfirmed) return;

      APIMATERIALMGMT.DeleteIndent(selectedRowIds[0]) // single-id delete in your old API
        .then((resp) => resp.json())
        .then((data) => {
          if (data.statusCode === 204) {
            const selectedData = gridRef.current.api.getSelectedRows();
            gridRef.current.api.applyTransaction({ remove: selectedData });
            setAlertTitleText("Success", "The record was deleted successfully");

            const remaining = (rows || []).filter(
              (r) => !selectedRowIds.includes(r.id)
            );
            dispatch(setRows(remaining));
            setSelectedRowId(0);
            setSelectedRowIds([]);
            dispatch(saveGridState({ selectedIds: [] }));
          } else {
            setAlertTitleText(
              "Oops...",
              `Something went wrong...${data.message}`
            );
          }
        });
    });
  };

  const onGo = () => {
    debouncedLoadIndents(dateValue, toDateValue);
  };

  return (
    <SideNav
      currentComponent={MATERIAL_MANAGEMENT}
      subComponent={INDENT}
      subComponentPath={PATH_INDENT}
      detailComponent=""
    >
      <div
        style={{
          display: "flex",
          flexDirection: "row-reverse",
          marginBottom: 10,
          marginRight: 10,
        }}
      >
        <Button
          style={{
            backgroundColor: SPANISH_GREEN,
            textTransform: "capitalize",
            fontSize: 14,
            color: WHITE,
            minWidth: "180px",
            minHeight: "45px",
          }}
          onClick={() => {
            captureGridState();
            navigate(PATH_INDENT_DETAILS + 0, {
              state: {
                date: (dateValue || dayjs()).format("YYYY-MM-DD"),
                isEdit: null,
              },
            });
          }}
        >
          {ADD}
        </Button>

        <Button
          onClick={deleteModel}
          variant="outlined"
          sx={{ borderColor: SPANISH_GREEN }}
          style={{
            textTransform: "capitalize",
            fontSize: 14,
            color: SPANISH_GREEN,
            minWidth: "180px",
            minHeight: "45px",
            marginRight: 10,
          }}
        >
          {DELETE}
        </Button>
      </div>

      {/* filter bar */}
      <div
        style={{
          backgroundColor: LIGHT_GREY_BACKGROUND,
          borderRadius: "5px",
          minWidth: "90%",
          minHeight: "50px",
          padding: 20,
          marginBottom: 10,
          borderStyle: "solid",
          border: "1x",
          borderColor: "#eaeaea",
          display: "flex",
        }}
      >
        <div style={{ display: "flex" }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DesktopDatePicker
              id="dateId"
              label="From Date"
              value={dateValue}
              onChange={(newValue) => setDateValue(newValue)}
              inputFormat="DD-MM-YYYY"
              renderInput={(params) => <TextField {...params} />}
            />
          </LocalizationProvider>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DesktopDatePicker
              id="toDateId"
              label="To Date"
              value={toDateValue}
              onChange={(newValue) => setToDateValue(newValue)}
              inputFormat="DD-MM-YYYY"
              renderInput={(params) => (
                <TextField {...params} style={{ marginLeft: 10 }} />
              )}
            />
          </LocalizationProvider>
        </div>
        <div
          align="right"
          style={{
            width: "100%",
            right: 0,
          }}
        >
          <Button
            disabled={isLoading}
            style={{
              backgroundColor: SPANISH_GREEN,
              textTransform: "capitalize",
              fontSize: 14,
              color: WHITE,
              minWidth: "129px",
              minHeight: "45px",
              marginTop: 5,
              opacity: isLoading ? 0.6 : 1,
            }}
            onClick={onGo}
          >
            {GO}
          </Button>
        </div>
      </div>

      {/* grid */}
      <div className="App">
        <div className="ag-theme-alpine" style={{ height: "570px" }}>
          <AgGridReact
            copyGroupHeadersToClipboard={true}
            enableRangeSelection={true}
            columnDefs={columnDefs}
            ref={gridRef}
            defaultColDef={defaultColDef}
            onGridReady={onGridReady}
            paginationAutoPageSize={true}
            pagination={true}
            rowSelection="multiple"
            getRowId={(params) => params.data.id}
            onSelectionChanged={onRowSelectionChanged}
            suppressBrowserResizeObserver={true}
          />
        </div>
      </div>
    </SideNav>
  );
}
